<script setup lang="ts">
//#region imports
import { computed, nextTick, onBeforeMount, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter, type RouteLocationNormalizedGeneric } from 'vue-router';
import {
  addDays,
  endOfDay,
  endOfISOWeek,
  endOfMonth,
  endOfYear,
  format,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  startOfYear,
  subDays,
  subHours,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { VueDraggable as Draggable } from 'vue-draggable-plus';
import { useTypedStore } from '@/store';
import { useLs } from '@/setup/vueLs';
import { useLodash } from '@/setup/lodash';
import { useApi } from '@/api';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useEventBus } from '@/setup/eventBus';
import AddButton from '@/components/AddButton.vue';
import EditableHeader from '@/views/Dashboard/components/EditableHeader.vue';
import DashboardMetricStat from '@/views/Dashboard/components/DashboardMetricStat.vue';
import DashboardMetricChart from '@/views/Dashboard/components/DashboardMetricChart.vue';
import DashboardMetricTable from '@/views/Dashboard/components/DashboardMetricTable.vue';
import DashboardGlobalFilter from '@/views/Dashboard/components/DashboardGlobalFilter.vue';
import updateUrl from '@/service/updateUrl';
import randomizer from '@/helpers/randomizer';
import { EDIT_MODE, GET_DASHBOARD_BY_ID, GET_DASHBOARDS, SET_DASHBOARD_FILTER } from '@/store/action_types/dashboards';
import { type DateRange, type FilterRecord } from '@/types/core';
import { useDashboardFilter } from '@/composables/useDashboardFilter';
import { usePopupManager } from '@/components/PopupManager.vue';
import type { AvaliableLocales } from '@/i18n';
import type {
  PostDashboardsData,
  PutDashboardsByIdUpdateTimeframeData,
  PutDashboardsByIdData,
  PostDashboardsStatsResponse,
} from '@/types/openapi-ts';
import type {
  Dashboard,
  DashboardPanel,
  DashboardPanelChart,
  DashboardPanelTable,
  DashboardPanelTypes,
  DashboardRow,
  FilterRecordExtendedCustom,
} from '@/types/dashboard';
//#endregion

//#region types
interface DateSettings {
  pickerDateRange: DateRange,
  shortcutName: string,
}

export interface Props {
  typeView?: string;
}

type PopupType = 'cancel' | 'delete' | 'default';

interface PopupParams {
  [key: string]: {
    title: string;
    message: string;
    confirmTitle: string;
    confirmIcon: string;
    bodyIcon: string;
    successMethod: () => void;
  };
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  typeView: '',
});
//#endregion

//#region const
const LS_KEY = 'Dashboard';
//#endregion
//#region composables
const store = useTypedStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const ls = useLs();
const api = useApi();
const _ = useLodash();
const {
  currencyCode,
  customerCurrencies,
  checkCurrencyCode,
} = useFormatCurrency();
const detectPermissions = useDetectPermissions();
const eventBus = useEventBus();
const {
  decodeFilterSelect,
  parseFilter,
} = useDashboardFilter();
//#endregion

//#region state
const cloneDateSettings = ref<DateSettings>({} as DateSettings);
const updatePiker = ref('');
const isChangeDatepicker = ref(false);
const shortcutName = ref('last_30_days');
const pickerDateRange =  ref<DateRange>([]);
const isDefault = ref(false);
const cloneDashboard = ref<Dashboard>({} as Dashboard);
const isLoadingDashboard = ref(false);
const dashboard = ref<Dashboard>({} as Dashboard);
const drag = ref(false);
const editMode = ref(false);
const intervalReload = ref<NodeJS.Timeout>();
const loadAllItem = ref(false);
const disabledRefresh = ref(false);
const dataRouteTo = ref<RouteLocationNormalizedGeneric>();
const filters = ref<FilterRecordExtendedCustom>({});
const filterFilters = ref<FilterRecordExtendedCustom>({});
const searchFilters = ref<FilterRecord>({});
const random = ref('');
const refreshPeriod = ref(1800);
const editOnce = ref(false);
const rerenderGlobalFilter = ref('');
const disabledCloneBtn = ref(false);

const statRef = useTemplateRef('stat');
const chartRef = useTemplateRef('chart');
const tableRef = useTemplateRef('table');
//#endregion

//#region computed
const overviewDashboard = computed(() => store.getters['dashboards/overviewDashboard']);
const dashboards = computed(() => store.getters['dashboards/dashboards']);
const dashboardById = computed(() => store.getters['dashboards/dashboardById']);
const disabledRequestStats = computed(() => store.getters['other/disabledRequestStats']);

const rowNameIsEmpty = computed(() => dashboard.value.rows.some((row) => row.name === ''));

const autorefreshSelect = computed(() => [300, 600, 900, 1800, 3600].map((sec) => ({
  value: sec,
  label: t(`dashboards.autorefresh.${sec}`),
})));

const templateDashboard = computed(() => ({
  id: '',
  name: t('dashboards.other.new_dashboard'),
  refresh_period_seconds: refreshPeriod.value,
  relative_timeframe: 'last_30_days',
  custom_timeframe_from: pickerDateRange.value[0],
  custom_timeframe_to: pickerDateRange.value[1],
  is_default: false,
  filters: {},
  rows: [],
} as Dashboard));

const checkOverviewTemplate = computed(() => route.path === '/dashboards/overview');

const computedDashboard = computed(() => {
  const currentDashboard = getCurrentDashbord();
  const newPanels: DashboardRow['panels'][] = [];

  currentDashboard.rows.forEach((row, index) => {
    newPanels[index] = row.panels.reduce(
      (acc, val) => {
        if (val.type === 'stat') {
          acc.stat.push({ ...val });
          return acc;
        }
        if (val.type === 'table') {
          acc.table.push({ ...val });
          return acc;
        }
        /* TODO: if fix backed */
        //@ts-expect-error тип series непостоянен
        if (!Array.isArray(val.series)) val.series = [];

        acc.chart.push({ ...val });

        return acc;
      },
      { stat: [], table: [], chart: []} as DashboardRow['panels'],
    );
  });

  const sortPanel = currentDashboard.rows.map((row, ind) => ({
    ...row,
    panels: newPanels[ind],
  }));

  return { ...currentDashboard, rows: sortPanel } as Dashboard;
});

const checkRoute = computed(() => route.params.id);

const queryParamsDashboard = computed<PutDashboardsByIdData['body']>(() => {
  const rows = dashboard.value.rows.map((row) => {
    const panels = [
      ...row.panels.stat as PutDashboardsByIdData['body']['rows'][number]['panels'],
      ...row.panels.chart as PutDashboardsByIdData['body']['rows'][number]['panels'],
      ...row.panels.table as PutDashboardsByIdData['body']['rows'][number]['panels'],
    ].map((panel) => {
      return _.omit(panel, ['id', 'report']) as PutDashboardsByIdData['body']['rows'][number]['panels'][number];
    });
    return {
      name: row.name,
      panels,
    };
  });
  return {
    name: dashboard.value.name,
    refresh_period_seconds: refreshPeriod.value.toString() as PutDashboardsByIdData['body']['refresh_period_seconds'],
    filters: computedFilters.value,
    relative_timeframe: shortcutName.value || 'custom',
    custom_timeframe_from: format(startOfDay(pickerDateRange.value[0] as Date), 'yyyy-MM-dd HH:mm:ss'),
    custom_timeframe_to: format(endOfDay(pickerDateRange.value[1] as Date), 'yyyy-MM-dd HH:mm:ss'),
    rows,
  } as PutDashboardsByIdData['body'];
});

const disabledSave = computed(() => {
  let disabled = false;
  if (dashboard.value.rows.length === 0) return true;
  dashboard.value.rows.forEach((row) => {
    if (
      row.panels.stat.length === 0
      && row.panels.chart.length === 0
      && row.panels.table.length === 0
    ) {
      disabled = true;
    }
  });
  return disabled;
});

const computedFilters = computed(() => ({
  ...parseFilter(filterFilters.value),
  search: searchFilters.value,
}));
//#endregion

//#region methods
function handleEditDashboard() {
  cloneDashboard.value = _.cloneDeep(dashboard.value);
  editMode.value = true;
  isDefault.value = dashboard.value.is_default;
  cloneDateSettings.value = {
    pickerDateRange: pickerDateRange.value,
    shortcutName: shortcutName.value,
  };
}

function pickerOptions() {
  const today = new Date();
  return [
    {
      label: t('ui.shortcuts.relative_time_ranges'),
      value: {
        short_title: 'group',
        type: 'group-title',
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.last_24_hours'),
      value: {
        range: [subHours(today, 24), today],
        short_title: 'last_24_hours',
        type: 'picker',
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.last_2_days'),
      value: {
        range: [subDays(today, 1), today],
        short_title: 'last_2_days',
        type: 'picker',
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.last_7_days'),
      value: {
        short_title: 'last_7_days',
        range: [subDays(today, 6), today],
        type: 'picker',
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.last_30_days'),
      value: {
        type: 'picker',
        short_title: 'last_30_days',
        range: [subDays(today, 29), today],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.last_90_days'),
      value: {
        type: 'picker',
        short_title: 'last_90_days',
        range: [subDays(today, 89), today],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.last_6_months'),
      value: {
        type: 'picker',
        short_title: 'last_6_months',
        range: [addDays(subMonths(today, 6), 1), today],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.last_1_year'),
      value: {
        type: 'picker',
        short_title: 'last_1_year',
        range: [addDays(subYears(today, 1), 1), today],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.other_quick_ranges'),
      value: {
        short_title: 'group',
        type: 'group-title',
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.today'),
      value: {
        type: 'picker',
        short_title: 'today',
        range: [startOfDay(today), endOfDay(today)],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.today_so_far'),
      value: {
        type: 'picker',
        short_title: 'today_so_far',
        range: [startOfDay(today), today],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.yesterday'),
      value: {
        type: 'picker',
        short_title: 'yesterday',
        range: [startOfDay(subDays(today, 1)), endOfDay(subDays(today, 1))],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.day_before_yesterday'),
      value: {
        type: 'picker',
        short_title: 'day_before_yesterday',
        range: [startOfDay(subDays(today, 2)), endOfDay(subDays(today, 2))],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.this_day_last_week'),
      value: {
        type: 'picker',
        short_title: 'this_day_last_week',
        range: [startOfDay(subWeeks(today, 1)), endOfDay(subWeeks(today, 1))],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.this_week'),
      value: {
        type: 'picker',
        short_title: 'this_week',
        range: [startOfISOWeek(today), endOfISOWeek(today)],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.this_week_so_far'),
      value: {
        type: 'picker',
        short_title: 'this_week_so_far',
        range: [startOfISOWeek(today), today],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.previous_week'),
      value: {
        type: 'picker',
        short_title: 'previous_week',
        range: [startOfISOWeek(subWeeks(today, 1)), endOfISOWeek(subWeeks(today, 1))],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.previous_month'),
      value: {
        type: 'picker',
        short_title: 'previous_month',
        range: [startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.previous_year'),
      value: {
        type: 'picker',
        short_title: 'previous_year',
        range: [startOfYear(subYears(today, 1)), endOfYear(subYears(today, 1))],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.this_month'),
      value: {
        type: 'picker',
        short_title: 'this_month',
        range: [startOfMonth(today), endOfMonth(today)],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.this_month_so_far'),
      value: {
        type: 'picker',
        short_title: 'this_month_so_far',
        range: [startOfMonth(today), today],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.this_year'),
      value: {
        type: 'picker',
        short_title: 'this_year',
        range: [startOfYear(today), endOfYear(today)],
      },
      slot: 'preset-date-range-button',
    },
    {
      label: t('ui.shortcuts.this_year_so_far'),
      value: {
        type: 'picker',
        short_title: 'this_year_so_far',
        range: [startOfYear(today), today],
      },
      slot: 'preset-date-range-button',
    },
  ];
}

function _query () {
  return {
    currency_code: currencyCode.value,
  };
}

function _completedFilterUrl() {
  const _dataFilters = _query();
  updateUrl.updateGetParams(_dataFilters);
}

function changeCurrency(_currencyCode: AvaliableLocales) {
  currencyCode.value = _currencyCode;
}

function getCurrentDashbord() {
  return checkRoute.value
    ? dashboards.value.find((d) => d.id === checkRoute.value)!
    : overviewDashboard.value![0]!;
}

function openCloneConfirm() {
  const currentDashboard = getCurrentDashbord();

  usePopupManager().cloneDashboardPopup.open({
    data: {
      cloneName: currentDashboard.name,
    },
    listeners: {
      success: (name) => {
        cloneDashboardHandler(name);
      },
    },
  });
}

async function cloneDashboardHandler(cloneName: string) {
  const { id } = dashboard.value;

  try {
    await store.dispatch(`dashboards/${GET_DASHBOARD_BY_ID}`, id);
  } catch (error) {
    eventBus.$emit('notify-event', {
      type: 'error',
      text: t('dashboards.other.clone_danger'),
    });
  } finally {
    disabledCloneBtn.value = false;
  }

  const currentDashboard = {
    ...dashboardById.value,
    /* TODO: можно удалить если поправят валидацию на вервере. series */
    rows: dashboardById.value!.rows.map((row) => ({
      ...row,
      panels: row.panels.map((panel) => {
        if (panel.type === 'chart' && 'series' in panel) {
          return {
            ...panel,
            series: panel.series === undefined ? [] : panel.series,
          };
        }
        return panel;
      }),
    })),
  };

  const query = {
    ...(_.pick(currentDashboard, [
      'custom_timeframe_from',
      'custom_timeframe_to',
      'filters',
      'is_default',
      'refresh_period_seconds',
      'relative_timeframe',
      'rows',
    ]) as object),
    name: cloneName,
  } as PostDashboardsData['body'];

  try {
    disabledCloneBtn.value = true;
    await api.createDashboards(query);
    setTimeout(() => {
      eventBus.$emit('notify-event', {
        type: 'info',
        text: `${t('dashboards.other.clone_success')} ${cloneName}`,
      });
    }, 2500);
    await getDashboard('only');
  } catch (error) {
    eventBus.$emit('notify-event', {
      type: 'error',
      text: t('dashboards.other.clone_danger'),
    });
  } finally {
    disabledCloneBtn.value = false;
  }
}

// TODO: чекнуть типы
function changeForecast(rowIndex: number, type: DashboardPanelTypes, index: number, forecast: boolean) {
  (dashboard.value.rows[rowIndex]!.panels[type][index] as DashboardPanelChart).enable_forecast = forecast;
}

async function updateDashboardTimeframe()  {
  const params: PutDashboardsByIdUpdateTimeframeData['body'] = {
    relative_timeframe: shortcutName.value as PutDashboardsByIdUpdateTimeframeData['body']['relative_timeframe'] || 'custom',
    custom_timeframe_from: pickerDateRange.value[0] as string,
    custom_timeframe_to: pickerDateRange.value[1] as string,
  };

  await api.updateDashboardTimeframe(dashboard.value.id, params);
}

function datepickerBlur() {
  if (!isChangeDatepicker.value) {
    updatePiker.value = randomizer();
  }
}

async function selectDateRange(range: DateRange, shortName: string) {
  if (range) {
    isChangeDatepicker.value = true;
    pickerDateRange.value = range;
    shortcutName.value = shortName;
    if (props.typeView !== 'add' && !editMode.value) {
      await updateDashboardTimeframe();
    }
    nextTick(() => {
      updateDashboardElements(undefined, true);
      isChangeDatepicker.value = false;
    });
  }
}

function changeAutorefresh(val: number) {
  refreshPeriod.value = val;
}

function cancelDashboard() {
  /* datepicker change */
  pickerDateRange.value = cloneDateSettings.value.pickerDateRange;
  shortcutName.value = cloneDateSettings.value.shortcutName;
  setDateParams({
    relative_timeframe: shortcutName.value,
    custom_timeframe_from: pickerDateRange.value[0],
    custom_timeframe_to: pickerDateRange.value[1],
  } as Dashboard);

  datepickerBlur();
  /* <--- datepicker change */

  dashboard.value = _.cloneDeep(cloneDashboard.value);
  refreshPeriod.value = dashboard.value.refresh_period_seconds;
  editMode.value = false;
  store.commit(`dashboards/${EDIT_MODE}`, editMode.value);
  window.onbeforeunload = null;
  editOnce.value = false;
  initGlobalFilters();
  rerenderGlobalFilter.value = randomizer();
  updateDashboardElements('getLocalData');
}

function handleGlobalFilter(filter: FilterRecordExtendedCustom, search: FilterRecord) {
  filterFilters.value = filter;
  searchFilters.value = search;
  store.commit(
    `dashboards/${SET_DASHBOARD_FILTER}`,
    computedFilters.value,
  );
  updateDashboardElements();
}

function redirectIfNotHomePage() {
  if (
    overviewDashboard.value.length === 0
    && route.path === '/dashboards/overview'
    && dashboards.value.length
  ) {
    router.replace(`/dashboards/view/${dashboards.value[0]!.id}`);
    return 'break';
  }
  if (
    overviewDashboard.value.length === 0
    && route.path !== '/dashboards/add-dashboard'
    && dashboards.value.length === 0
  ) {
    router.push('/dashboards/add-dashboard');
    return 'break';
  }
  return 'continue';
}

function setHomeDashboard() {
  isDefault.value = !isDefault.value;
  store.commit(`dashboards/${EDIT_MODE}`, editMode.value);
  window.onbeforeunload = null;
  editOnce.value = true;
}

function confirmDataPopup(popupType: PopupType = 'default') {
  const popupManager = usePopupManager();

  const popupParams: PopupParams = {
    cancel: {
      title: t('dashboards.confirm.cancel_title'),
      message: t('dashboards.confirm.cancel_text'),
      confirmTitle: t('dashboards.confirm.cancel_btn_сonfirm'),
      confirmIcon: 'check',
      bodyIcon: 'exclamation-triangle',
      successMethod: cancelDashboard,
    },
    delete: {
      title: t('dashboards.confirm.delete_title'),
      message: t('dashboards.confirm.delete_text', { name: dashboard.value.name }),
      confirmTitle: t('dashboards.confirm.delete_btn_сonfirm'),
      confirmIcon: 'trash',
      bodyIcon: 'trash-alt',
      successMethod: deleteDashboardSuccess,
    },
    default: {
      title: t('dashboards.confirm.title'),
      message: t('dashboards.confirm.text'),
      confirmTitle: t('dashboards.confirm.btn_сonfirm'),
      confirmIcon: 'trash',
      bodyIcon: 'exclamation-triangle',
      successMethod: confirmHandler,
    },
  };

  const confirmPopup = popupManager.confirm.open({
    data: {
      title: popupParams[popupType]!.title,
      message: popupParams[popupType]!.message,
      confirmTitle: popupParams[popupType]!.confirmTitle,
      bodyIcon: popupParams[popupType]!.bodyIcon,
      confirmIcon: popupParams[popupType]!.confirmIcon,
      bodyIconColor: 'var(--text-negative)',
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          if (popupParams[popupType]?.successMethod) {
            popupParams[popupType].successMethod();
          }
        } catch (error) {
          window.console.error(error);
        } finally {
          context.handleClose();
        }
      },
    },
  });
}

async function deleteDashboardSuccess() {
  editMode.value = false;
  store.commit(`dashboards/${EDIT_MODE}`, false);
  window.onbeforeunload = null;
  editOnce.value = false;
  if (overviewDashboard.value.length + dashboards.value.length === 1) {
    router.replace('/dashboards/add-dashboard');
  } else if (
    overviewDashboard.value.length === 0
    && dashboards.value.length > 1
  ) {
    router.replace(
      `/dashboards/view/${
        dashboards.value[dashboards.value[0]!.id === checkRoute.value ? 1 : 0]!.id
      }`,
    );
  } else {
    router.replace('/dashboards/overview');
  }
  await deleteDashboard();
}

function confirmHandler() {
  store.commit(`dashboards/${EDIT_MODE}`, false);
  window.onbeforeunload = null;
  if (dataRouteTo.value) {
    router.push(dataRouteTo.value.fullPath);
  }
}

function confirmEditDashboards(to: RouteLocationNormalizedGeneric) {
  dataRouteTo.value = to;
  confirmDataPopup();
}

async function handleRefresh() {
  loadAllItem.value = false;
  setDisabledBtnRefresh();
  await store.dispatch('reports/getSettings');
  await getDashboard();
  initGlobalFilters();

  updateDashboardElements(undefined, true);
  setDashboardInterval(true);
}

function setDisabledBtnRefresh() {
  const delay = 10000;
  disabledRefresh.value = true;
  setTimeout(() => {disabledRefresh.value = false;}, delay);
}

function setDashboardInterval(reload = false) {
  if (reload) clearInterval(intervalReload.value);
  const interval = refreshPeriod.value * 1000;

  intervalReload.value = setInterval(() => {
    setDateParams({
      relative_timeframe: shortcutName.value,
      custom_timeframe_from: pickerDateRange.value[0],
      custom_timeframe_to: pickerDateRange.value[1],
    } as Dashboard);
    updateDashboardElements();
  }, interval);
}

async function deleteDashboard() {
  await api.deleteDashboard(dashboard.value.id);
  await getDashboard('only');
}

async function changeLocale() {
  if (!editMode.value) await getDashboard();
  updateDashboardElements();
}

function removeDashboardRow(rowIndex: number) {
  dashboard.value.rows.splice(rowIndex, 1);
  updateDashboardElements('getLocalData');
}

function changeDraggable() {
  random.value = randomizer();
  updateDashboardElements('getLocalData');
}

function updateStat(type?: string, force = false) {
  if (!statRef.value) return;

  statRef.value.forEach((elem) => {
    if(elem) {
      if (force) elem.loading = true;
      elem.getStat(type);
    }
  });
}

function updateChart(type?: string, force = false) {
  if (!chartRef.value) return;

  chartRef.value.forEach((elem) => {
    if(elem) {
      if (force) elem.loading = true;
      elem.getReport(type!);
    }
  });
}

function updateTable(type?: string, force = false) {
  if (!tableRef.value) return;

  tableRef.value.forEach((elem) => {
    if (elem) {
      if (force) elem.loading = true;
      elem.getReport(type);
    }
  });
}

function inputTitleRow(name: string, index: number) {
  dashboard.value.rows[index]!.name = name;
  updateDashboardElements('getLocalData');
}

function addDashboardRow() {
  dashboard.value.rows.push({
    name: t('dashboards.other.default_section'),
    panels: {
      stat: [],
      chart: [],
      table: [],
    },
  });
}

async function editDashboard() {
  editMode.value = false;
  store.commit(`dashboards/${EDIT_MODE}`, editMode.value);
  window.onbeforeunload = null;
  await api.updateDashboard(
    dashboard.value.id,
    queryParamsDashboard.value,
  );
  if (isDefault.value) {
    await api.setDefaultDashboard(dashboard.value.id);
  }

  if (isDefault.value && route.path !== '/dashboards/overview') {
    router.push('/dashboards/overview');
    return;
  }

  await getDashboard();

  updateDashboardElements();
}

/* prop method */
function updateDashboardElements(type?: string, force = false) {
  nextTick(() => {
    updateStat(type, force);
    updateChart(type, force);
    updateTable(type, force);
  });
}

async function createDashboard() {
  try {
    const {
      data: { payload },
    } = await api.createDashboards(queryParamsDashboard.value);
    const { id } = payload;
    if (isDefault.value) await api.setDefaultDashboard(id);
    editMode.value = false;
    store.commit(`dashboards/${EDIT_MODE}`, editMode.value);
    window.onbeforeunload = null;

    await getDashboard('only');
    router.push(
      isDefault.value ? '/dashboards/overview' : `/dashboards/view/${id}`,
    );
  } catch (error) {
    router.push('/dashboards/overview');
  }
}

function setReportData(
  rowIndex: number,
  type: DashboardPanelTypes,
  index: number,
  report: PostDashboardsStatsResponse['payload'],
) {
  dashboard.value.rows[rowIndex]!.panels[type][index]!.report = report;
}

function removeMetric(rowIndex: number, type: DashboardPanelTypes, index: number) {
  dashboard.value.rows[rowIndex]!.panels[type]!.splice(index, 1);
  updateDashboardElements('getLocalData');
}

function addMetricFromPopup(type: string, rowIndex: number, metricConfig: DashboardPanel, index: number) {
  const typeMetric: DashboardPanelTypes = metricConfig.type;
  if (type === 'add') {
    dashboard.value.rows[rowIndex]!.panels[typeMetric].push(metricConfig);
  } else {
    dashboard.value.rows[rowIndex]!.panels[typeMetric][index] = metricConfig;
  }
  updateDashboardElements('getLocalData');
}

function handleAddMetricRow(rowIndex: number) {
  usePopupManager().addDashboardMetricsPopup.open({
    data: {
      period: pickerDateRange.value,
      propCurrencyCode: currencyCode.value,
      rowIndex: rowIndex,
    },
    listeners: {
      success: (popupType, rowIndex, dashboardStatComputed, indexEditMetric) => {
        addMetricFromPopup(popupType, rowIndex, dashboardStatComputed, indexEditMetric);
      },
    },
  });
}

function editMetric(rowIndex: number, type: string, index: number, data: DashboardPanel) {
  usePopupManager().addDashboardMetricsPopup.open({
    data: {
      popupType: 'edit',
      data: data,
      period: pickerDateRange.value,
      propCurrencyCode: currencyCode.value,
      rowIndex: rowIndex,
      index: index,
    },
    listeners: {
      success: (popupType, rowIndex, dashboardStatComputed, indexEditMetric) => {
        addMetricFromPopup(popupType, rowIndex, dashboardStatComputed, indexEditMetric);
      },
    },
  });
}

function setDateParams(dashboard = {} as Dashboard) {
  if (dashboard.relative_timeframe === 'custom') {
    shortcutName.value = dashboard.relative_timeframe;
    pickerDateRange.value = [
      format(startOfDay(dashboard.custom_timeframe_from!), 'yyyy-MM-dd HH:mm:ss'),
      format(endOfDay(dashboard.custom_timeframe_to!), 'yyyy-MM-dd HH:mm:ss'),
    ];

    return;
  }

  const findShortcut = pickerOptions().find(
    (sc) => sc.value.short_title === dashboard.relative_timeframe,
  );

  if (findShortcut) {
    shortcutName.value = findShortcut.value.short_title;
    pickerDateRange.value = findShortcut.value.range!;

    return;
  }

  shortcutName.value = pickerOptions()[4]!.value.short_title;
  pickerDateRange.value = pickerOptions()[4]!.value.range!;
}

async function initDashboard() {
  editMode.value = false;
  try {
    isLoadingDashboard.value = true;
    await getDashboard();
  } finally {
    isLoadingDashboard.value = false;
  }
}

async function getDashboard(only?: string) {
  await store.dispatch(`dashboards/${GET_DASHBOARDS}`);
  if (only) return;

  dashboard.value = _.cloneDeep(computedDashboard.value);

  setDateParams(dashboard.value);

  refreshPeriod.value = dashboard.value.refresh_period_seconds;

  initGlobalFilters();
  rerenderGlobalFilter.value = randomizer();
  updateStat();
}

function initGlobalFilters() {
  filters.value = decodeFilterSelect(dashboard.value.filters || {});
  filterFilters.value = _.cloneDeep(filters.value);
  searchFilters.value = dashboard.value.filters?.search || {};
  store.commit(
    `dashboards/${SET_DASHBOARD_FILTER}`,
    computedFilters.value,
  );

  rerenderGlobalFilter.value = randomizer();
}
//#endregion

//#region watch
watch(currencyCode, (val) => {
  ls.set(`${LS_KEY}/currency_code`, val);
  _completedFilterUrl();
});

watch(
  dashboard,
  (value) => {
    if (editMode.value) {
      editOnce.value = true;
      window.onbeforeunload = () => true;
      store.commit(`dashboards/${EDIT_MODE}`, editMode.value);
      refreshPeriod.value = value.refresh_period_seconds;
    }
  },
  {
    deep: true,
  },
);

watch(editMode, (value) => {
  if (value) {
    clearInterval(intervalReload.value);
  } else {
    setDashboardInterval();
  }
});

watch(checkRoute, async () => {
  const _break = redirectIfNotHomePage();
  if (_break === 'break') return;

  loadAllItem.value = false;
  isDefault.value = dashboard.value.is_default;
  await initDashboard();
  updateDashboardElements(undefined, true);
});
//#endregion

//#region hooks
onBeforeMount(async () => {
  if (detectPermissions.checkRequest('reports_view')) {
    const _urlData = updateUrl.getParseParams(route.hash);

    if (!_.isEmpty(_urlData)) {
      currencyCode.value = checkCurrencyCode(_.get(_urlData, ['currency_code']) as string);
    } else {
      currencyCode.value = checkCurrencyCode(ls.get(`${LS_KEY}/currency_code`));
    }

    loadAllItem.value = false;

    const _break = redirectIfNotHomePage();

    if (_break !== 'break') {
      if (props.typeView === 'add') {
        store.commit(
          `dashboards/${SET_DASHBOARD_FILTER}`,
          {},
        );
        dashboard.value = templateDashboard.value;
        setDateParams();
        dashboard.value = _.cloneDeep(dashboard.value);
        isDefault.value = false;
        nextTick(() => (editMode.value = props.typeView === 'add'));
      } else {
        await initDashboard();
        editMode.value = false;
        updateDashboardElements(undefined, true);
        setDashboardInterval();
        isDefault.value = dashboard.value.is_default;
      }

      eventBus.$on('confirmEditDashboards', confirmEditDashboards);
      eventBus.$on('changeLocale', changeLocale);
    }
  }
  detectPermissions.checkRoute('reports_view');
});

onUnmounted(() => {
  eventBus.$off('confirmEditDashboards', confirmEditDashboards);
  eventBus.$off('changeLocale', changeLocale);

  clearInterval(intervalReload.value);
});
//#endregion
</script>

<template>
  <div
    :class="[
      'dashboard-view t:space-y-24',
      { 'dashboard-view_edit': editMode }
    ]"
  >
    <div class="t:space-y-12">
      <div class="t:flex t:items-center t:justify-between">
        <div class="t:flex">
          <UiTitle
            v-if="!editMode"
            :font-size="24"
            class="t:font-medium"
          >
            {{ dashboard.name }}
          </UiTitle>
          <EditableHeader
            v-else
            class="dashboard-view__title-edit"
            :value="dashboard.name"
            :is-delete="false"
            @update="(text: string) => dashboard.name = text"
          />
        </div>
        <div class="t:flex t:space-x-8">
          <template
            v-if="!editMode"
          >
            <UiButton
              icon="pen"
              :disabled="isLoadingDashboard || disabledRequestStats"
              @click="handleEditDashboard"
            >
              {{ $t('dashboards.other.edit_dashboard') }}
            </UiButton>
            <UiButton
              icon="clone"
              :disabled="isLoadingDashboard || disabledRequestStats || disabledCloneBtn"
              @click="openCloneConfirm"
            >
              {{ $t('dashboards.other.clone') }}
            </UiButton>
            <UiButton
              icon="sync-alt"
              :disabled="disabledRefresh || disabledRequestStats"
              @click="handleRefresh"
            >
              {{ $t('dashboards.other.refresh') }}
            </UiButton>
          </template>
          <template
            v-if="typeView === 'add'"
          >
            <UiButton
              icon="times"
              @click="$router.push('/dashboards/overview')"
            >
              {{ $t('dashboards.other.cancel') }}
            </UiButton>
            <UiButton
              icon="check"
              filled
              :disabled="disabledSave || !dashboard.name || rowNameIsEmpty"
              @click="createDashboard"
            >
              {{ $t('dashboards.other.save_dashboard') }}
            </UiButton>
          </template>
          <template
            v-if="typeView !== 'add' && editMode"
          >
            <UiButton
              icon="trash-alt"
              type="negative"
              filled
              :disabled="checkOverviewTemplate"
              @click="confirmDataPopup('delete')"
            >
              {{ $t('dashboards.other.delete') }}
            </UiButton>
            <UiButton
              icon="times"
              @click="editOnce ? confirmDataPopup('cancel') : cancelDashboard()"
            >
              {{ $t('dashboards.other.cancel') }}
            </UiButton>
            <UiButton
              icon="check"
              filled
              :disabled="disabledSave || !dashboard.name || rowNameIsEmpty"
              @click="editDashboard"
            >
              {{ $t('dashboards.other.save_changes') }}
            </UiButton>
          </template>
        </div>
      </div>

      <UiSeparator />
      <div class="t:flex t:items-center t:justify-between">
        <div
          class="t:flex t:space-x-8"
        >
          <UiDatePicker
            v-model="pickerDateRange"
            :disabled="disabledRequestStats"
            :preset-dates-factory="pickerOptions"
            :default-value="4"
            :shortcut-name="shortcutName"
            custom-input
            @update:model-value="selectDateRange"
          />
          <DashboardGlobalFilter
            v-model="filters"
            :set-current-search="searchFilters"
            :disabled-mode="disabledRequestStats"
            @submit="handleGlobalFilter"
          />
          <UiButton
            v-if="editMode && !dashboard.is_default"
            :type="!isDefault ? 'positive' : 'negative'"
            :icon-sub-style="!isDefault ? 'fas' : 'fad'"
            :icon="!isDefault ? 'house' : 'house-damage '"
            @click="setHomeDashboard"
          >
            {{ isDefault ? $t('dashboards.other.dont_set_as_default') : $t('dashboards.other.set_as_default') }}
          </UiButton>
        </div>
        <div
          class="t:flex t:space-x-8"
        >
          <div
            v-if="editMode"
            class="t:flex t:items-center t:space-x-8"
          >
            <span class="dashboard-view__autorefresh-title">{{ $t('dashboards.autorefresh.title') }}</span>
            <UiSelectElement
              v-model="refreshPeriod"
              :list="autorefreshSelect"
              option-value-param="value"
              option-name-param="label"
              option-label-param="label"
              width="90"
              @change="changeAutorefresh"
            />
          </div>
          <UiCurrencyPicker
            :model-value="currencyCode"
            :currencies="customerCurrencies"
            @update:model-value="changeCurrency"
          />
        </div>
      </div>
      <div
        v-if="editMode"
        class="dashboard-view__alert"
      >
        <UiIcon
          name="info-circle"
          color="var(--text-positive)"
          class="t:mr-8"
        />
        {{ $t('dashboards.other.edit_alert') }}
      </div>
    </div>
    <Draggable
      v-if="!isLoadingDashboard"
      v-model="dashboard.rows"
      :group="`description`"
      :disabled="!editMode"
      class="t:space-y-16"
      ghost-class="dashboard-view__panel_draggable-shadow"
      handle=".dashboard-view__panel-title"
      :force-fallback="true"
      @change="changeDraggable"
      @start="drag = true"
      @end="drag = false"
    >
      <div
        v-for="(row, rowIndex) in dashboard.rows"
        :key="rowIndex + row.name"
        class="dashboard-view__panel"
        :class="{ 'dashboard-view__panel_draggable': drag }"
      >
        <div class="t:space-y-16">
          <div class="dashboard-view__panel-title">
            <UiTitle
              v-if="!editMode"
              :key="rowIndex + row.name"
              :font-size="20"
            >
              {{ row.name }}
            </UiTitle>
            <EditableHeader
              v-else
              :key="`editable_${rowIndex + row.name}`"
              is-move
              :value="row.name"
              @delete="removeDashboardRow(rowIndex)"
              @update="inputTitleRow($event, rowIndex)"
            />
          </div>
          <AddButton
            v-if="editMode"
            @click="handleAddMetricRow(rowIndex)"
          >
            {{ $t('dashboards.other.add_metric') }}
          </AddButton>
          <Draggable
            v-if="row.panels.stat.length > 0"
            v-model="row.panels.stat"
            :force-fallback="true"
            :group="`description-stats`"
            :disabled="false"
            ghost-class="dashboard-view__panel-group-item_draggable-shadow"
            class="dashboard-view__panel-group dashboard-view__panel-group_stat"
            draggable=".dashboard-view__panel-group-item_draggable"
            @change="changeDraggable"
            @start="drag = true"
            @end="drag = false"
          >
            <DashboardMetricStat
              v-for="(stat, index) in row.panels.stat"
              :key="stat.id"
              ref="stat"
              :class="[
                'dashboard-view__panel-group-item',
                {'dashboard-view__panel-group-item_draggable': editMode},
              ]"
              :prop-currency-code="currencyCode"
              :stat="stat as DashboardPanelTable"
              :is-edit="editMode"
              :index="index"
              :row-index="rowIndex"
              :period="pickerDateRange"
              @remove-metric="removeMetric"
              @edit-metric="editMetric"
              @emit-data-report="setReportData"
            />
          </Draggable>
          <Draggable
            v-if="row.panels.chart.length > 0"
            v-model="row.panels.chart"
            :force-fallback="true"
            :group="`description-chart`"
            :disabled="false"
            ghost-class="dashboard-view__panel-group-item_draggable-shadow"
            class="dashboard-view__panel-group dashboard-view__panel-group_chart"
            draggable=".dashboard-view__panel-group-item_draggable"
            @change="changeDraggable"
            @start="drag = true"
            @end="drag = false"
          >
            <DashboardMetricChart
              v-for="(chart, index) in row.panels.chart"
              :key="chart.id"
              ref="chart"
              :class="[
                'dashboard-view__panel-group-item',
                {'dashboard-view__panel-group-item_draggable': editMode},
              ]"
              :chart="chart"
              :prop-currency-code="currencyCode"
              :index="index"
              :row-index="rowIndex"
              :is-edit="editMode"
              :save-period="!editMode"
              :animation="!editMode"
              :period="pickerDateRange"
              :dashboard-id="dashboard.id"
              @change-forecast="changeForecast"
              @edit-metric="editMetric"
              @remove-metric="removeMetric"
              @emit-data-report="setReportData"
            />
          </Draggable>
          <Draggable
            v-if="row.panels.table.length > 0"
            v-model="row.panels.table"
            :force-fallback="true"
            :group="`description-table`"
            :disabled="false"
            ghost-class="dashboard-view__panel-group-item_draggable-shadow"
            class="dashboard-view__panel-group dashboard-view__panel-group_table"
            draggable=".dashboard-view__panel-group-item_draggable"
            @change="changeDraggable"
            @start="drag = true"
            @end="drag = false"
          >
            <DashboardMetricTable
              v-for="(table, index) in row.panels.table"
              :key="table.id"
              ref="table"
              :class="[
                'dashboard-view__panel-group-item',
                {'dashboard-view__panel-group-item_draggable': editMode},
              ]"
              :period="pickerDateRange"
              :prop-currency-code="currencyCode"
              :table="table"
              :index="index"
              :row-index="rowIndex"
              :is-edit="editMode"
              @emit-data-report="setReportData"
              @edit-metric="editMetric"
              @remove-metric="removeMetric"
            />
          </Draggable>
        </div>
      </div>
    </Draggable>
    <div
      v-if="editMode"
      class="dashboard-view__panel-add"
      @click="addDashboardRow"
    >
      <UiIcon
        size="16px"
        name="plus"
        color="inherit"
        class="t:mr-8"
      />
      <UiTitle
        :font-size="20"
      >
        {{ $t('dashboards.other.new_section') }}
      </UiTitle>
    </div>
  </div>
</template>

<style lang='scss'>
.dashboard-view {
  padding: 16px 0;

  &__title-edit .editable-header__contenteditable {
    font-weight: 500;
    font-size: 24px;
  }

  &__alert{
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    padding: 8px;
    background-color: var(--background-positive-subtle);
    color: var(--text-positive);
    font-size: 12px;
    line-height: 15px;
  }

  &__autorefresh-title {
    font-size: 14px;
  }

  &__panel-add {
    display: flex;
    align-items: center;
    cursor: pointer;
    color: var(--text-primary);
  }

  &_edit &{
    &__panel-title,
    &__panel-group-item{
      cursor: pointer;
    }
  }
  &__panel_draggable &{
    &__panel-title,
    &__panel-group-item{
      cursor: grabbing;
    }
  }

  &__panel_draggable-shadow {
    opacity: 1;
  }

  &__panel-group{
    &_chart,
    &_table {
      width: 100%;
      min-height: 20px;
      display: grid;
      gap: 24px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin-bottom: 24px;
    }
    &_stat {
      width: 100%;
      min-height: 20px;
      display: grid;
      gap: 24px;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      margin-bottom: 24px;
    }
  }

  &__panel-group-item {
    &_draggable-shadow {
      opacity: .3;
      background-color: var(--text-primary);

      * {
        visibility: hidden;
      }
    }
  }

  @media (min-width: 2000px) {
    &__panel-group{
      &_chart,
      &_table {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
      &_stat {
        grid-template-columns: repeat(6, minmax(0, 1fr));
      }
    }
  }

  @media (max-width: 1400px) {
    &__panel-group{
      &_stat {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
    }
  }
}
</style>
